import {
  assertArray,
  assertBoolean,
  assertInteger,
  assertNonEmptyString,
  assertOneOf,
  assertOptionalInteger,
  assertOptionalRecord,
  assertOptionalString,
  assertRecord,
  assertString,
} from '@fleece/utilities';
import { Credential, CredentialCheck, CREDENTIAL_KINDS, CredentialMetadata, CredentialSummary, FEED_STATES, FeedStatus } from '../credential';
import { Notifier, NOTIFIER_TYPES, NotifierSecret } from '../notifier';
import { CredentialListing } from './credential-api';

/** Reading the credential and notifier responses back into the models. See `wire.ts` for why this exists. */

function reviveMetadata(record: Record<string, unknown>, field: string): CredentialMetadata {
  const checkedAt = assertOptionalInteger(record['checkedAt'], `${field}.checkedAt`);
  const invalid = assertOptionalRecord(record['invalid'], `${field}.invalid`);
  return {
    credentialId: assertNonEmptyString(record['credentialId'], `${field}.credentialId`),
    name: assertString(record['name'], `${field}.name`),
    version: assertInteger(record['version'], `${field}.version`),
    createdAt: assertInteger(record['createdAt'], `${field}.createdAt`),
    updatedAt: assertInteger(record['updatedAt'], `${field}.updatedAt`),
    ...(checkedAt === undefined ? {} : { checkedAt }),
    ...(invalid === undefined
      ? {}
      : { invalid: { since: assertInteger(invalid['since'], `${field}.invalid.since`), reason: assertString(invalid['reason'], `${field}.invalid.reason`) } }),
  };
}

export function reviveCredentialCheck(value: unknown, field = 'check'): CredentialCheck {
  const record = assertRecord(value, field);
  const kind = assertOneOf(record['kind'], `${field}.kind`, CREDENTIAL_KINDS);
  const checkedAt = assertInteger(record['checkedAt'], `${field}.checkedAt`);
  if (!assertBoolean(record['valid'], `${field}.valid`)) {
    return { kind, valid: false, reason: assertString(record['reason'], `${field}.reason`), checkedAt };
  }
  switch (kind) {
    case 'alpaca':
      return {
        kind,
        valid: true,
        accountId: assertNonEmptyString(record['accountId'], `${field}.accountId`),
        accountStatus: assertString(record['accountStatus'], `${field}.accountStatus`),
        checkedAt,
      };
    case 'polygon':
      return { kind, valid: true, checkedAt };
  }
}

export function reviveCredentialSummary(value: unknown, field = 'credential'): CredentialSummary {
  const record = assertRecord(value, field);
  const kind = assertOneOf(record['kind'], `${field}.kind`, CREDENTIAL_KINDS);
  const metadata = reviveMetadata(record, field);
  switch (kind) {
    case 'alpaca':
      return {
        ...metadata,
        kind,
        accountId: assertNonEmptyString(record['accountId'], `${field}.accountId`),
        live: assertBoolean(record['live'], `${field}.live`),
        supportData: assertBoolean(record['supportData'], `${field}.supportData`),
        trackOrders: assertBoolean(record['trackOrders'], `${field}.trackOrders`),
        defaultAccountId: assertNonEmptyString(record['defaultAccountId'], `${field}.defaultAccountId`),
        accessKeyHint: assertString(record['accessKeyHint'], `${field}.accessKeyHint`),
      };
    case 'polygon':
      return { ...metadata, kind, apiKeyHint: assertString(record['apiKeyHint'], `${field}.apiKeyHint`) };
  }
}

/** The one response that carries the secrets. */
export function reviveCredential(value: unknown, field = 'credential'): Credential {
  const record = assertRecord(value, field);
  const summary = reviveCredentialSummary(value, field);
  switch (summary.kind) {
    case 'alpaca':
      return {
        ...summary,
        accessKey: assertNonEmptyString(record['accessKey'], `${field}.accessKey`),
        secretKey: assertNonEmptyString(record['secretKey'], `${field}.secretKey`),
      };
    case 'polygon':
      return { ...summary, apiKey: assertNonEmptyString(record['apiKey'], `${field}.apiKey`) };
  }
}

export function reviveFeedStatus(value: unknown, field = 'feed'): FeedStatus {
  const record = assertRecord(value, field);
  const error = assertOptionalString(record['error'], `${field}.error`);
  return { state: assertOneOf(record['state'], `${field}.state`, FEED_STATES), ...(error === undefined ? {} : { error }) };
}

export function reviveCredentialListing(value: unknown, field = 'credential'): CredentialListing {
  const record = assertRecord(value, field);
  const credential = reviveCredentialSummary(record['credential'], `${field}.credential`);
  return record['feed'] === undefined ? { credential } : { credential, feed: reviveFeedStatus(record['feed'], `${field}.feed`) };
}

export function reviveCredentialListings(value: unknown, field = 'credentials'): ReadonlyArray<CredentialListing> {
  return assertArray(value, field).map((entry, index) => reviveCredentialListing(entry, `${field}[${index}]`));
}

export function reviveNotifier(value: unknown, field = 'notifier'): Notifier {
  const record = assertRecord(value, field);
  const target = assertRecord(record['target'], `${field}.target`);
  const description = assertOptionalString(record['description'], `${field}.description`);
  return {
    notifierId: assertNonEmptyString(record['notifierId'], `${field}.notifierId`),
    name: assertString(record['name'], `${field}.name`),
    ...(description === undefined ? {} : { description }),
    target: {
      type: assertOneOf(target['type'], `${field}.target.type`, NOTIFIER_TYPES),
      channel: assertString(target['channel'], `${field}.target.channel`),
      webhookId: assertString(target['webhookId'], `${field}.target.webhookId`),
    },
    version: assertInteger(record['version'], `${field}.version`),
    createdAt: assertInteger(record['createdAt'], `${field}.createdAt`),
    updatedAt: assertInteger(record['updatedAt'], `${field}.updatedAt`),
  };
}

export function reviveNotifiers(value: unknown, field = 'notifiers'): ReadonlyArray<Notifier> {
  return assertArray(value, field).map((entry, index) => reviveNotifier(entry, `${field}[${index}]`));
}

export function reviveNotifierSecret(value: unknown, field = 'secret'): NotifierSecret {
  const record = assertRecord(value, field);
  return { type: assertOneOf(record['type'], `${field}.type`, NOTIFIER_TYPES), webhookUrl: assertNonEmptyString(record['webhookUrl'], `${field}.webhookUrl`) };
}
